import type { ScheduleTRPCInput } from '@risksmart-app/trpc/src/types';
import type {
  UpdateChildObligationInput,
  UpdateObligationMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTRPC } from 'src/utils/trpc';

type UpdateObligationTRPCInput = Omit<UpdateChildObligationInput, 'schedule'> &
  ScheduleTRPCInput;

/**
 * Maps the TRPC mutation response to match the GraphQL mutation structure.
 */
const mapTrpcResponseToGraphQL = (trpcData: {
  Id: string;
}): UpdateObligationMutation => {
  return {
    updateChildObligation: {
      __typename: 'IdOutput',
      Id: trpcData.Id,
    },
  };
};

export const useUpdateObligationTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const mutation = useMutation({
    ...trpc.frontend.obligation.update.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.getById.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.register.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.obligationsByType.queryKey(),
        });
        await queryClient.invalidateQueries({
          queryKey: trpc.frontend.obligation.obligationList.queryKey(),
        });
      },
    }),
  });

  return {
    updateObligation: async (
      variables: UpdateObligationTRPCInput
    ): Promise<UpdateObligationMutation> => {
      await mutation.mutateAsync({
        ...variables,
        Adherence: variables.Adherence ?? '',
      });

      return mapTrpcResponseToGraphQL({ Id: variables.Id });
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
